import type {
  CreateBookOrderCastPayload,
  CreateBookOrderPayload,
} from '../../use-create-book-order';
import type { CastTextEntry } from '../../state/book-configurator-store';

/**
 * LightGateOverlay's pure payload helpers.
 *
 * Kept in a `.ts` (not `.tsx`) file next to the overlay so Vite's
 * React-Refresh rule (`react-refresh/only-export-components`) stays
 * green: the overlay `.tsx` exports only the component; these pure
 * helpers export nothing renderable.
 */

export interface CastPhotoFiles {
  readonly face: File | null;
}

export interface UploadPlan {
  readonly files: readonly File[];
  /** For each cast key, the index into `files` for the face photo. */
  readonly indexByCast: ReadonlyMap<string, { readonly face: number }>;
}

export function buildUploadPlan(
  cast: readonly CastTextEntry[],
  photos: ReadonlyMap<string, CastPhotoFiles>,
): UploadPlan {
  const files: File[] = [];
  const indexByCast = new Map<string, { face: number }>();
  for (const entry of cast) {
    const p = photos.get(entry.key);
    if (p === undefined || p.face === null) {
      throw new Error(`buildUploadPlan: cast ${entry.key} has no face photo — upload plan invalid`);
    }
    const faceIdx = files.length;
    files.push(p.face);
    indexByCast.set(entry.key, { face: faceIdx });
  }
  return { files, indexByCast };
}

export function buildCreateOrderPayload(params: {
  readonly cast: readonly CastTextEntry[];
  readonly plan: UploadPlan;
  readonly photoKeys: readonly string[];
  readonly freitext: string;
  readonly sprache: string;
  /**
   * Block 2 commit 1 (Sebastian ruling 2026-08-31) — optional
   * book-level title wish. Passed through to the DTO's
   * `titelwunsch` field. Empty string → omitted from payload → no
   * seed of storyText.title → anchor-title service fires normally.
   */
  readonly titelwunsch: string;
  /**
   * PROTOTYPE – feat/story-step-stations-prototype (Sebastian GO
   * 2026-10-08 round 2). `null` → omit the `storyKind` field from
   * the payload (back-compat); a defined value lands at
   * `BookOrder.intake.raw.storyKind` on the backend.
   */
  readonly storyKind: 'erlebt' | 'erfunden' | null;
}): CreateBookOrderPayload {
  const { cast, plan, photoKeys, freitext, sprache, titelwunsch, storyKind } = params;
  const castPayload: CreateBookOrderCastPayload[] = cast.map((entry) => {
    const idx = plan.indexByCast.get(entry.key)!;
    if (entry.ageYears === null) {
      throw new Error(`buildCreateOrderPayload: cast ${entry.key} has no ageYears`);
    }
    // Block 2 commit 1 (Sebastian ruling 2026-08-31) — three new
    // per-person fields: personType (Person/Tier, default 'person'),
    // geschlecht (männlich/weiblich/keineAngabe, default
    // 'keineAngabe'), and optional personenNotiz (200-char free text).
    // The `isProtagonist` field is DELETED end-to-end.
    const trimmedNotiz = entry.personenNotiz.trim();
    // Position-2 Schritt B (Sebastian ruling 2026-09-11) — concrete
    // species trimmed on send when personType === 'tier'. Person
    // entries omit the field entirely (DTO @ValidateIf skips
    // validation when personType !== 'tier'). Empty tier species is
    // gated at the PWA — the Weiter button stays disabled — but the
    // defensive omission here keeps the payload clean if the gate
    // somehow yields.
    const trimmedSpecies = entry.species.trim();
    const speciesField =
      entry.personType === 'tier' && trimmedSpecies.length > 0
        ? { species: trimmedSpecies }
        : {};
    // Sebastian ruling 2026-09-19 — the empty-string sentinel `''`
    // for `geschlecht` marks an unset person entry. Save is gated on
    // this, so `''` should never reach the payload builder in
    // practice. Defense-in-depth: coerce to `'keineAngabe'` if it
    // ever does, so the backend DTO (which accepts only the three
    // real values) does not reject.
    const geschlechtForWire =
      entry.geschlecht === '' ? 'keineAngabe' : entry.geschlecht;
    return {
      key: entry.key,
      name: entry.name,
      ageYears: entry.ageYears,
      facePhotoKey: photoKeys[idx.face]!,
      personType: entry.personType,
      geschlecht: geschlechtForWire,
      ...(trimmedNotiz.length > 0 ? { personenNotiz: trimmedNotiz } : {}),
      ...speciesField,
    };
  });
  const trimmedTitelwunsch = titelwunsch.trim();
  return {
    description: freitext,
    // Bau-Spez v3.1 §6 line 367 places Buchart + Größe in Schritt 5
    // (Bestellung, Phase 6). Commit 3 sends 'standard' as a placeholder;
    // Phase 6 will update the row. See tracker
    // `[book-create-order-size-variant-placeholder]`.
    sizeVariant: 'standard',
    // §A3.6 Schauplätze prose is not collected in Schritt 1 today —
    // the Freitext block covers general description. `stadt: null`
    // suppresses the resolver's landmark path per its null-stadt guard.
    stadt: null,
    cast: castPayload,
    language: sprache,
    ...(trimmedTitelwunsch.length > 0 ? { titelwunsch: trimmedTitelwunsch } : {}),
    ...(storyKind !== null ? { storyKind } : {}),
  };
}
